import {
  computed,
  h,
  defineComponent,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from 'vue';
import { SelectOptions } from '@/ui/types';
import { emptyIssue, IssueRecordEditable } from '@/ui/pages/issue/issueTypes';
import { IssueDetail } from '@/entities/issue/v2';
import { Assignee } from '@/entities/assignee';
import { useTask } from '@/ui/concerns';
import { filter as getAllUsers } from '@/services/api/resources/user';
import {
  filterListToOptions,
  sequence,
  filterAssigneesListToOptionsUsers,
  filterListToEditableGroups,
  filterListToEditableUsers,
} from '@/ui/helpers';
import { get, intoEditable, intoSendable, update } from '@/services/api/resources/issue/v2';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { versioned } from '@/services/api';
import { user } from '@/ui/auth/auth';
import { IssueStatus } from '@/entities/todo';
import { t } from '@/ui/i18n/i18n';
import { useIssueForm } from '@/ui/pages/issue/useIssueForm';
import { IssueType } from '@/entities/issue';
import { button, loader, message } from '@/ui/components';
import { IssueDetailsModal } from '@/ui/pages/issue/IssueDetailsModal';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';
import { useEntityItemSelection } from '@/ui/pages/entityItem/useEntityItemSelection';
import { filter as getAllGroups } from '@/services/api/resources/group';
import { createUserIdentifierFromActionPageKey } from '@/ui/injectionKeys';
import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
import { ConfirmationModal } from '@/ui/components/modals/ConfirmationModal';
import { useModal } from '@/ui/concerns/modals/useModal';
import { useAssignees } from '@/ui/concerns/useAssignees';

interface IssueEditProps {
  id: number;
  hideSubmitButton?: boolean;
}

export const IssueEdit = defineComponent<IssueEditProps>({
  name: 'IssueEdit',
  props: ['id', 'hideSubmitButton'],
  setup(props, context) {
    provide(createUserIdentifierFromActionPageKey, CreateUserModelIdentifier.actionCreator);
    const isIssueDetailModalOpen = ref(false);

    const [isResetApprovalModalOpen, openResetApprovalModal, closeResetApprovalModal] = useModal();

    const openResetApprovalModalOrSubmit = () => {
      if (recordEditable.value.approval === null || recordEditable.value.status === IssueStatus.Done) {
        handleSubmit();

        return;
      }

      openResetApprovalModal();
    };

    const onModalToggle = (value: boolean) => {
      isIssueDetailModalOpen.value = value;
    };

    const entityItemOptions = ref<SelectOptions>([]);
    const deletedUserOptions = ref<SelectOptions<string>>([]);

    // create empty record
    const recordEditable = ref<IssueRecordEditable>(emptyIssue());
    const recordIssueDetail = ref<IssueDetail>();

    let recordVersion = '';

    const {
      disableEntityItemSelection,
      canSeeAllEntityItems,
    } = useEntityItemSelection(user, entityItemOptions);

    const shouldShowSeeDetailsModal = computed(() => (
      recordIssueDetail.value
      && !(recordIssueDetail.value!.data.type === IssueType.ManualForm
        || recordIssueDetail.value!.data.type === IssueType.AutomaticForm)
    ));

    const {
      assigneeOptions,
      fillAssigneeOptions,
      fillGroupsOptions,
      getUsers,
    } = useAssignees();

    const fillDeletedUserOptions = (
      assignees: Array<Assignee>,
      dataUsers: { data: BaseFilterEntity[] },
    ) => {
      const dataUsersIds = dataUsers.data.map(dataUser => dataUser.id);
      deletedUserOptions.value = filterAssigneesListToOptionsUsers(
        assignees.filter(assignee => !dataUsersIds.includes(assignee.id)),
      );
    };

    // get record
    const getLegacyRecord = useTask(
      () => sequence(
        get(props.id),
        getAllEntityItems(),
        getAllUsers(),
        getAllGroups(),
      ),
      ([{
        data,
        version,
      }, entityItems, users, groups]) => {
        context.emit('issue:fetch', data);
        entityItemOptions.value = filterListToOptions(entityItems).map(disableEntityItemSelection);
        fillGroupsOptions(groups);
        fillAssigneeOptions(users);
        recordEditable.value = intoEditable(data);
        recordIssueDetail.value = data;
        recordVersion = version;
        fillDeletedUserOptions(data.data.assignees, users);
      },
    );

    // send record
    const sendRecord = useTask(
      () => {
        recordEditable.value.assignees = filterListToEditableUsers(recordEditable.value.assignees);
        recordEditable.value.assignedGroups = filterListToEditableGroups(recordEditable.value.assignedGroups);

        return update(props.id, versioned(intoSendable(recordEditable.value, user.value.timezone), recordVersion));
      },
      () => {
        context.emit('edited', recordEditable.value);
      },
    );

    // form
    const {
      formFields,
      handleSubmit,
      submitIsLoading,
    } = useIssueForm(recordEditable, sendRecord);
    const disabledExternalAssignee = computed(
      () => [IssueStatus.Done, IssueStatus.CantDo].includes(recordEditable.value.status),
    );

    const hasAuthorizedEntityItemSelected = computed(
      () => {
        if (recordEditable.value.entityItem === null || canSeeAllEntityItems()) {
          return true;
        }

        return user.value.entityItems.some(entityItem => entityItem.id === recordEditable.value.entityItem);
      },
    );

    const registerBusEvents = () => {
      EventBus.$on(Events.updateIssue, handleSubmit);
      EventBus.$on(Events.seeMoreDetailsIssue, onModalToggle);
    };

    const unRegisterBusEvents = () => {
      EventBus.$off(Events.updateIssue, handleSubmit);
      EventBus.$off(Events.seeMoreDetailsIssue, onModalToggle);
    };

    onMounted(registerBusEvents);
    onUnmounted(unRegisterBusEvents);
    watch(ref(props.id), getLegacyRecord.run);

    return function render() {
      return loader('form', getLegacyRecord, [
        ...formFields(
          entityItemOptions.value,
          assigneeOptions.value,
          disabledExternalAssignee.value,
          true,
          getUsers,
          deletedUserOptions.value,
        ),
        hasAuthorizedEntityItemSelected.value
          ? null
          : message('error', t('issues.is-not-assigned-to-entity-item')),
        props.hideSubmitButton
          ? null
          : button(
            t('issues.update'),
            openResetApprovalModalOrSubmit,
            { 'data-test': 'update-button' },
            {
              loading: submitIsLoading.value,
              disabled: !hasAuthorizedEntityItemSelected.value,
            },
          ),
        shouldShowSeeDetailsModal.value
          ? h(IssueDetailsModal, {
            props: {
              issue: recordIssueDetail.value,
              active: isIssueDetailModalOpen.value,
            },
            on: { close: () => onModalToggle(false) },
          })
          : null,
        h(ConfirmationModal, {
          class: 'reset-key-confirmation-modal',
          props: {
            active: isResetApprovalModalOpen.value,
            confirmText: t('approve.modal.reset.confirm'),
            declineText: t('approve.modal.reset.decline'),
            confirmationButtonProps: { type: 'is-info', loading: false },
          },
          on: {
            declined: closeResetApprovalModal,
            accepted: handleSubmit,
          },
        }, [
          h('div', t('approve.modal.reset.content-status')),
        ]),
      ]);
    };
  },
});

export function issueEdit(props: IssueEditProps, on: Record<string, Function> = {}) {
  return h(IssueEdit, {
    props,
    on,
  });
}
